import { useState } from 'react';
import { Checkbox, TableCell, TableRow, CircularProgress, Box } from '@mui/material';
import ArrowFwdIcon from '../assets/nav-arrow-fwd.svg';
import CustomButton from '../reusable/CustomButton';
import { useNavigate } from 'react-router-dom';
import { useTranscriptSelectionOutletContext } from '../hooks/useTranscriptSelectionOutletContext';
import { selectTranscript } from './utilityFunctions';
import useUserDetails from '../hooks/useUserDetails';
import useSchools from '../hooks/useSchools';
import useErrorModal from '../hooks/useErrorModal';
import { USER_ROLES } from '../../constants';
import { Transcript, TranscripsCategoriesKeys } from '../../interfaces/TranscriptsInterface';
import { trimTo2Decimals } from '../../utils';

interface TranscriptSelectionTableRowProps {
  row: Transcript;
  rowIndex: number;
  transcripts: Transcript[];
  category: TranscripsCategoriesKeys;
  updateDetailsTranscripts: () => void;
}

type TableCellValues =
  | 'school_student_number'
  | 'gpa'
  | 'credits'
  | 'course_intensity_score'
  | 'race'
  | 'gender'
  | 'student_with_disabilities'
  | 'english_learner'
  | 'low_income';

const tableCells: TableCellValues[] = [
  'school_student_number',
  'gpa',
  'credits',
  'course_intensity_score',
  'race',
  'gender',
  'student_with_disabilities',
  'english_learner',
  'low_income',
];

function TranscriptSelectionTableRow({
  row,
  rowIndex,
  transcripts,
  category,
  updateDetailsTranscripts,
}: TranscriptSelectionTableRowProps) {
  const {
    selectedTranscriptsSummaryResponse: { selectedTranscriptsSummary, setSelectedTranscriptsSummary },
    setPreviewTranscriptIndex,
    workflowStatus,
    refreshWorkflowStatus,
  } = useTranscriptSelectionOutletContext();
  const { transcriptSelectionSchool } = useSchools();
  const { handleOpen: handleOpenErrorModal } = useErrorModal();
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const userRole = userDetails?.role;
  const isFacilitator = userRole === USER_ROLES.FACILITATOR;
  const isLeadFacilitator = userRole === USER_ROLES.LEAD_FACILITATOR;
  const isAdmin = userRole === USER_ROLES.XQ_ADMIN;

  const canEdit =
    (workflowStatus === 'TRANSCRIPT_SELECTION_IN_PROGRESS' || workflowStatus === 'TRANSCRIPT_SELECTION_NOT_STARTED') &&
    (isFacilitator || isLeadFacilitator);

  const showCheckbox = canEdit || isAdmin;

  const selectedTranscriptsIdsByCategory = selectedTranscriptsSummary.selectedTranscriptsByCategory[category];

  const navigate = useNavigate();
  const isSelected = Boolean(selectedTranscriptsIdsByCategory.find((transcript) => transcript.id === row.id));
  const [isLoading, setIsLoading] = useState(false);

  const handleSelectTranscript = async () => {
    setIsLoading(true);
    try {
      await selectTranscript(
        userDetails,
        category,
        row,
        isSelected,
        transcriptSelectionSchool,
        handleOpenErrorModal,
        setSelectedTranscriptsSummary,
        workflowStatus,
        refreshWorkflowStatus
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoToTranscriptDetails = () => {
    setPreviewTranscriptIndex(rowIndex);
    updateDetailsTranscripts();
    navigate('/transcripts/details');
  };

  const defaultTableCellValues = Object.fromEntries(
    tableCells
      .filter((key) => !['gpa', 'school_student_number', 'profile_name'].includes(key))
      .map((key) => [key, row[key as keyof Transcript]?.toString().toLowerCase()])
  );

  const tableCellValues: { [key: string]: string | number } = {
    ...defaultTableCellValues,
    gpa: trimTo2Decimals(row['gpa']),
    school_student_number: `Student ${row['school_student_number']}`,
  };

  return (
    <TableRow sx={{ height: '100px' }}>
      <TableCell>
        {showCheckbox && (
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <Checkbox checked={isSelected} onChange={handleSelectTranscript} disabled={!canEdit || isLoading} />
            {isLoading && (
              <CircularProgress
                size={24}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                }}
              />
            )}
          </Box>
        )}
      </TableCell>
      {tableCells.map((value) => {
        return (
          <TableCell key={value} align="left" sx={{ textTransform: 'capitalize' }}>
            {tableCellValues[value]}
          </TableCell>
        );
      })}
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        <CustomButton variant="clear" size="medium" buttonAction={handleGoToTranscriptDetails}>
          <img alt="go to transcript details" src={ArrowFwdIcon} />
        </CustomButton>
      </TableCell>
    </TableRow>
  );
}

export default TranscriptSelectionTableRow;
